// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Company basic info page component.
 * Displays detailed information about a company including billing information,
 * locations, and assets.
 */
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { EditRounded } from '@/constants/hardfin-standard-icons';
import { getCustomer } from '@/customer/api/get-customer';
import { CustomerCommentControl } from '@/customer/components/customer-comment-control';
import { CustomerErrorBoundary } from '@/customer/components/customer-error-boundary';
import { CustomerExternalIdEditor } from '@/customer/components/customer-external-id-editor';
import { EditBillingDetailsDialog } from '@/customer/components/edit-billing-details-dialog';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { isNil } from 'lodash-es';

function getLabel(value: string | null | undefined) {
  if (isNil(value)) {
    return 'Not set';
  }

  return value;
}

export const Route = createFileRoute('/_app/companies/$companyKey/')({
  component: CompanyIndexRoute,
});

function CompanyIndexRoute() {
  const { companyKey } = Route.useParams();

  return (
    <CustomerErrorBoundary>
      <CompanyIndexContent companyKey={companyKey} />
    </CustomerErrorBoundary>
  );
}

function CompanyIndexContent(props: { companyKey: string }) {
  const billingDialog = useDisclosure();

  const { data } = useSuspenseQuery({
    queryKey: ['customer', props.companyKey],
    queryFn: () => getCustomer(props.companyKey),
  });

  return (
    <>
      <EditBillingDetailsDialog
        customer={data.customer}
        isOpen={billingDialog.isOpen}
        onClose={billingDialog.close}
      />
      <HardfinTabCardWrapper title="Company information">
        <Box
          display="grid"
          gridTemplateColumns="3fr 7fr"
          gap={2}
          paddingTop={2}
        >
          {/* External ID and Comment Section - 30% */}
          <Stack paddingX={2} spacing={3}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">External ID</Typography>
              <CustomerExternalIdEditor customer={data.customer} />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Comment</Typography>
              <CustomerCommentControl customer={data.customer} />
            </Stack>
          </Stack>

          {/* Billing Details Paper - 70% */}
          <Paper variant="outlined" sx={{ alignSelf: 'start' }}>
            <Stack
              useFlexGap
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              paddingRight={2}
            >
              <Typography variant="subtitle2" margin={2}>
                Billing details
              </Typography>
              <IconButton size="small" onClick={billingDialog.open}>
                <EditRounded fontSize="small" />
              </IconButton>
            </Stack>
            <Divider />
            <Box margin={2}>
              <Box display="grid" gridTemplateColumns="3fr 4fr" gap={2}>
                {/* Billing Contact - 30% of total (3/10) */}
                <Stack spacing={1}>
                  <Typography variant="body2" fontWeight="medium">
                    Billing contact
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Name: {getLabel(data.customer.billingContact?.name)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Email: {getLabel(data.customer.billingContact?.email)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Phone: {getLabel(data.customer.billingContact?.phone)}
                  </Typography>
                </Stack>
                {/* Billing Address - 40% of total (4/10) */}
                <Stack spacing={1}>
                  <Typography variant="body2" fontWeight="medium">
                    Billing address
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    disabled
                    size="small"
                    variant="outlined"
                    value={data.customer.billingAddress ?? ''}
                    placeholder="Not set"
                    rows={3}
                  />
                </Stack>
              </Box>
            </Box>
          </Paper>
        </Box>
      </HardfinTabCardWrapper>
    </>
  );
}
